import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Spinner,
  Table,
} from "reactstrap";
import Swal from "sweetalert2";
import { getFirebaseAuthToken } from "../../../helpers/firebase_helper";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const authHeaders = async () => ({
  "Content-Type": "application/json",
  Authorization: `${await getFirebaseAuthToken()}`,
});

const api = async (path, { method = "GET", body } = {}) => {
  const res = await fetch(`${API_BASE}/dashboard/admin/partners${path}`, {
    method,
    headers: await authHeaders(),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`);
  return json;
};

const PAGE_SIZE = 50;

const fmtDay = (ms) => {
  if (!ms) return "—";
  try {
    return new Date(ms).toISOString().slice(0, 10);
  } catch {
    return "—";
  }
};

const STATUS_LABELS = {
  active: "Active",
  active_next_effective_date: "Scheduled",
  pending_activation: "Pending",
  terminated: "Terminated",
  terminated_next_effective_date: "Terminating",
};
const STATUS_COLORS = {
  active: "success",
  active_next_effective_date: "info",
  pending_activation: "secondary",
  terminated: "danger",
  terminated_next_effective_date: "warning",
};

/**
 * Coverage set-up state, one vocabulary for both enrollment modes:
 *   direct — BaxRx creates the eligibility: created | incomplete | waiting | failed
 *   portal — the member creates their account: enrolled | not_enrolled
 */
const ELIGIBILITY = {
  created: { label: "Covered", color: "success" },
  incomplete: { label: "Missing data", color: "warning" },
  waiting: { label: "Waiting", color: "secondary" },
  failed: { label: "Failed", color: "danger" },
  enrolled: { label: "Enrolled (portal)", color: "success" },
  not_enrolled: { label: "Not enrolled (portal)", color: "secondary" },
};

const eligibilityKey = (m) =>
  m.enrollmentMode === "direct"
    ? m.eligibility?.status || "waiting"
    : m.enrolled
      ? "enrolled"
      : "not_enrolled";

const EligibilityBadge = ({ member }) => {
  const k = eligibilityKey(member);
  const e = ELIGIBILITY[k] || { label: k, color: "secondary" };
  return (
    <Badge color={e.color} className="fw-normal">
      {e.label}
    </Badge>
  );
};

const toCsv = (rows, headers) => {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => esc(r[h])).join(",")),
  ].join("\n");
};

const download = (filename, text, type = "text/csv") => {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Member detail ──────────────────────────────────────────────────────────

const MemberDetailModal = ({ partnerId, externalId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setData(null);
    try {
      const d = await api(
        `/${partnerId}/members/${encodeURIComponent(externalId)}`,
      );
      setData(d);
      const mi = d.held?.memberInfo || {};
      setForm({
        firstName: d.employee.firstName || "",
        lastName: d.employee.lastName || "",
        email: d.employee.email || "",
        address1: mi.address1 || "",
        address2: mi.address2 || "",
        city: mi.city || "",
        state: mi.state || "",
        zipCode: mi.zipCode || "",
        gender: mi.gender || "",
        // Stored MMDDYYYY → YYYY-MM-DD for the date picker (and the API, which
        // takes ISO and converts to whatever the eligibility provider needs).
        dateOfBirth: mi.dob
          ? `${mi.dob.slice(4)}-${mi.dob.slice(0, 2)}-${mi.dob.slice(2, 4)}`
          : "",
        phone: mi.phone || "",
      });
    } catch (e) {
      Swal.fire("Could not load member", e.message, "error");
      onClose();
    }
  }, [partnerId, externalId, onClose]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (body, done) => {
    setBusy(true);
    try {
      const r = await api(
        `/${partnerId}/members/${encodeURIComponent(externalId)}`,
        {
          method: "PATCH",
          body,
        },
      );
      const el = r.employee?.eligibility;
      const warnings = r.employee?.warnings || [];
      Swal.fire({
        title: warnings.length ? "Saved, with problems" : "Saved",
        html:
          done +
          (el
            ? ` Eligibility: ${el.status}${el.memberId ? ` (${el.memberId})` : ""}.`
            : "") +
          (warnings.length
            ? `<ul class="text-start small mt-3 mb-0">${warnings
                .map((w) => `<li>${w.replace(/</g, "&lt;")}</li>`)
                .join("")}</ul>`
            : ""),
        icon: warnings.length ? "warning" : "success",
      });
      setEditing(false);
      await load();
      onChanged();
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const terminate = async () => {
    const { value, isConfirmed } = await Swal.fire({
      title: `Terminate ${data.employee.firstName} ${data.employee.lastName}?`,
      html:
        "Coverage ends on the date below (today or later). Their family's coverage ends with it." +
        (data.employee.enrollmentMode === "direct"
          ? "<br/><br/>No email is sent — this member is enrolled directly."
          : ""),
      input: "date",
      inputValue: new Date().toISOString().slice(0, 10),
      showCancelButton: true,
      confirmButtonText: "Terminate",
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;
    const ms = value ? Date.parse(`${value}T00:00:00Z`) : null;
    const today = Date.parse(
      `${new Date().toISOString().slice(0, 10)}T00:00:00Z`,
    );
    await patch(
      ms && ms > today
        ? { status: "terminated", terminationDate: ms }
        : { status: "terminated" },
      ms && ms > today
        ? `Termination scheduled for ${value}.`
        : "Member terminated.",
    );
  };

  const reactivate = () => patch({ status: "active" }, "Member reactivated.");

  const retry = async () => {
    setBusy(true);
    try {
      const r = await api(
        `/${partnerId}/members/${encodeURIComponent(externalId)}/retry-eligibility`,
        {
          method: "POST",
        },
      );
      const el = r.eligibility || {};
      Swal.fire(
        el.status === "created"
          ? "Eligibility created"
          : `Eligibility: ${el.status}`,
        el.error ||
          (el.missingFields?.length
            ? `Missing: ${el.missingFields.join(", ")}`
            : el.memberId || ""),
        el.status === "created" ? "success" : "warning",
      );
      await load();
      onChanged();
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  // Catch what the API would drop before sending it, with a reason in place.
  const formErrors = (() => {
    const errs = {};
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "Not a valid email";
    if (form.state && !/^[A-Za-z]{2}$/.test(form.state))
      errs.state = "2-letter state code, e.g. TX";
    if (form.zipCode && !/^\d{5}(-\d{4})?$/.test(form.zipCode))
      errs.zipCode = "5-digit ZIP";
    if (form.phone && form.phone.replace(/\D/g, "").length < 10)
      errs.phone = "10-digit phone number";
    if (form.dateOfBirth) {
      const d = Date.parse(`${form.dateOfBirth}T00:00:00Z`);
      if (Number.isNaN(d) || d > Date.now() || form.dateOfBirth < "1900-01-01")
        errs.dateOfBirth = "A real date in the past";
    }
    return errs;
  })();

  const saveEdit = () => {
    if (Object.keys(formErrors).length) return;
    const memberInfo = {};
    [
      "address1",
      "address2",
      "city",
      "state",
      "zipCode",
      "gender",
      "dateOfBirth",
      "phone",
    ].forEach((k) => {
      if (form[k]) memberInfo[k] = form[k];
    });
    patch(
      {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        memberInfo,
      },
      "Member updated.",
    );
  };

  const e = data?.employee;
  const el = data?.eligibility;
  const card = data?.cards?.coverageCard;
  const terminated = e?.status === "terminated";

  return (
    <Modal isOpen size="xl" toggle={onClose} scrollable>
      <ModalHeader toggle={onClose}>
        {e ? `${e.firstName} ${e.lastName}` : "Member"}{" "}
        {e && (
          <Badge
            color={STATUS_COLORS[e.status] || "secondary"}
            className="ms-2 fw-normal"
          >
            {STATUS_LABELS[e.status] || e.status}
          </Badge>
        )}
        {e && (
          <Badge color="light" className="text-muted ms-2 fw-normal">
            {e.enrollmentMode === "direct"
              ? "Direct enrollment"
              : "Portal enrollment"}
          </Badge>
        )}
      </ModalHeader>
      <ModalBody>
        {!data ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <div className="row g-4">
            <div className="col-lg-6">
              <h6 className="text-uppercase text-muted small">Member</h6>
              {!editing ? (
                <Table size="sm" borderless className="mb-3">
                  <tbody>
                    <tr>
                      <td className="text-muted">External ID</td>
                      <td>{e.externalId}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Email</td>
                      <td>{e.email}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Tier</td>
                      <td>{e.tier === "F" ? "Family" : "Employee"}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Address</td>
                      <td>
                        {data.held?.memberInfo
                          ? [
                              data.held.memberInfo.address1,
                              data.held.memberInfo.city,
                              data.held.memberInfo.state,
                              data.held.memberInfo.zipCode,
                            ]
                              .filter(Boolean)
                              .join(", ") || "—"
                          : "—"}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Gender / DOB</td>
                      <td>
                        {form.gender || "—"} /{" "}
                        {form.dateOfBirth
                          ? `${form.dateOfBirth.slice(5, 7)}/${form.dateOfBirth.slice(8)}/${form.dateOfBirth.slice(0, 4)}`
                          : "—"}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Phone</td>
                      <td>{form.phone || "—"}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Coverage</td>
                      <td>
                        {data.coverage.state} · from{" "}
                        {fmtDay(data.coverage.effectiveDate)}
                        {data.coverage.terminationDate
                          ? ` to ${fmtDay(data.coverage.terminationDate)}`
                          : ""}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              ) : (
                <div className="row g-2 mb-3">
                  {[
                    ["firstName", "First name"],
                    ["lastName", "Last name"],
                    ["email", "Email"],
                    ["address1", "Address"],
                    ["address2", "Address 2"],
                    ["city", "City"],
                    ["state", "State"],
                    ["zipCode", "ZIP"],
                    ["gender", "Gender"],
                    ["dateOfBirth", "Date of birth"],
                    ["phone", "Phone"],
                  ].map(([k, label]) => (
                    <div className="col-6" key={k}>
                      <Label className="small mb-1">{label}</Label>
                      {k === "gender" ? (
                        <Input
                          type="select"
                          bsSize="sm"
                          value={form.gender}
                          onChange={(ev) =>
                            setForm((f) => ({ ...f, gender: ev.target.value }))
                          }
                        >
                          <option value="">—</option>
                          <option value="F">Female</option>
                          <option value="M">Male</option>
                        </Input>
                      ) : (
                        <Input
                          bsSize="sm"
                          type={k === "dateOfBirth" ? "date" : "text"}
                          max={
                            k === "dateOfBirth"
                              ? new Date().toISOString().slice(0, 10)
                              : undefined
                          }
                          maxLength={k === "state" ? 2 : undefined}
                          invalid={!!formErrors[k]}
                          value={form[k]}
                          onChange={(ev) =>
                            setForm((f) => ({
                              ...f,
                              [k]:
                                k === "state"
                                  ? ev.target.value.toUpperCase()
                                  : ev.target.value,
                            }))
                          }
                        />
                      )}
                      {formErrors[k] && (
                        <div className="invalid-feedback d-block">
                          {formErrors[k]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <h6 className="text-uppercase text-muted small mt-3">
                Pharmacy eligibility
              </h6>
              {el ? (
                <Table size="sm" borderless className="mb-2">
                  <tbody>
                    <tr>
                      <td className="text-muted">Status</td>
                      <td>
                        <EligibilityBadge
                          member={{ enrollmentMode: "direct", eligibility: el }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Member ID</td>
                      <td className="fw-semibold">{el.memberId || "—"}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Window</td>
                      <td>
                        {el.startDate || "—"} →{" "}
                        {el.endDate === "9999-12-31"
                          ? "open-ended"
                          : el.endDate || "—"}
                      </td>
                    </tr>
                    {el.missingFields?.length > 0 && (
                      <tr>
                        <td className="text-muted">Missing</td>
                        <td className="text-warning">
                          {el.missingFields.join(", ")}
                        </td>
                      </tr>
                    )}
                    {el.error && (
                      <tr>
                        <td className="text-muted">Error</td>
                        <td className="text-danger small">{el.error}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="text-muted">Home delivery</td>
                      <td className="small">
                        {!el.homeDelivery?.requested ? (
                          <span className="text-muted">
                            Not requested (optional — retail coverage only)
                          </span>
                        ) : (
                          el.homeDelivery.people.map((p, i) => (
                            <div key={i}>
                              {p.name}
                              {p.personCode ? ` (${p.personCode})` : ""}:{" "}
                              <Badge
                                color={
                                  p.status === "set"
                                    ? "success"
                                    : p.status === "failed"
                                      ? "danger"
                                      : "secondary"
                                }
                                className="fw-normal"
                              >
                                {p.status === "set"
                                  ? "Ready"
                                  : p.status === "failed"
                                    ? "Failed"
                                    : "Pending"}
                              </Badge>
                              {p.error && (
                                <div className="text-danger">{p.error}</div>
                              )}
                            </div>
                          ))
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted small">
                  Portal member — their eligibility is created when they create
                  their BaxRx account
                  {e.enrolled ? " (done)." : " (not yet)."}
                </p>
              )}

              {data.todo?.length > 0 && (
                <>
                  <h6 className="text-uppercase text-muted small mt-3">
                    Open items
                  </h6>
                  <ul className="small ps-3">
                    {data.todo.map((t, i) => (
                      <li key={i}>
                        <code>{t.code}</code> — {t.message}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div className="col-lg-6">
              <h6 className="text-uppercase text-muted small">Coverage card</h6>
              {card ? (
                <>
                  <Table size="sm" bordered className="mb-2">
                    <tbody>
                      <tr>
                        <td className="text-muted">Member ID</td>
                        <td className="fw-semibold">
                          {card.memberPrefix}
                          {card.memberId}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Person code</td>
                        <td>{card.personCode}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">BIN</td>
                        <td>{card.bin}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">PCN</td>
                        <td>{card.pcn}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Group</td>
                        <td>{card.groupId}</td>
                      </tr>
                    </tbody>
                  </Table>
                  {data.cards?.coverageCardHtml && (
                    <iframe
                      title="Coverage card"
                      sandbox=""
                      srcDoc={data.cards.coverageCardHtml}
                      style={{
                        width: "100%",
                        height: 260,
                        border: "1px solid #e9ebec",
                        borderRadius: 6,
                      }}
                    />
                  )}
                </>
              ) : (
                <p className="text-muted small">
                  {data.cards?.coverageCardReason || "No coverage card."}
                </p>
              )}
              {data.cards?.coverageCardReason && card && (
                <p className="text-muted small mt-1">
                  {data.cards.coverageCardReason}
                </p>
              )}

              <h6 className="text-uppercase text-muted small mt-3">Family</h6>
              {data.family?.length ? (
                <Table size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Relationship</th>
                      <th>DOB</th>
                      <th>Person code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.family.map((f, i) => (
                      <tr key={i}>
                        <td>
                          {f.firstName} {f.lastName}
                        </td>
                        <td>{f.relationship}</td>
                        <td>
                          {f.dob
                            ? `${f.dob.slice(0, 2)}/${f.dob.slice(2, 4)}/${f.dob.slice(4)}`
                            : "—"}
                        </td>
                        <td>
                          {f.personCode || (
                            <span className="text-muted">not added yet</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted small">No family members.</p>
              )}
              {data.held?.dependentsError && (
                <p className="text-danger small mt-1">
                  {data.held.dependentsError}
                </p>
              )}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <div>
          {data &&
            e.enrollmentMode === "direct" &&
            el?.status !== "created" && (
              <Button
                color="primary"
                outline
                className="me-2"
                disabled={busy}
                onClick={retry}
              >
                Retry eligibility
              </Button>
            )}
        </div>
        <div>
          {data && !editing && (
            <>
              <Button
                color="light"
                className="me-2"
                disabled={busy}
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
              {terminated ? (
                <Button color="success" disabled={busy} onClick={reactivate}>
                  Reactivate
                </Button>
              ) : (
                <Button
                  color="danger"
                  outline
                  disabled={busy}
                  onClick={terminate}
                >
                  Terminate
                </Button>
              )}
            </>
          )}
          {editing && (
            <>
              <Button
                color="light"
                className="me-2"
                disabled={busy}
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={busy || Object.keys(formErrors).length > 0}
                onClick={saveEdit}
              >
                {busy ? "Saving…" : "Save"}
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

// ── Import CSV ─────────────────────────────────────────────────────────────

const ImportModal = ({ partnerId, groups, onClose, onDone }) => {
  const [groupExternalId, setGroupExternalId] = useState(
    groups.find((g) => g.enrollmentMode === "direct")?.externalId ||
      groups[0]?.externalId ||
      "",
  );
  const [file, setFile] = useState(null);
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const pick = async (f) => {
    setFile(f);
    setPreview(null);
    if (!f) return;
    const text = await f.text();
    setCsv(text);
    setBusy(true);
    try {
      setPreview(
        await api(`/${partnerId}/imports/preview`, {
          method: "POST",
          body: { csv: text },
        }),
      );
    } catch (e) {
      Swal.fire("Could not read the file", e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setBusy(true);
    try {
      const r = await api(`/${partnerId}/imports`, {
        method: "POST",
        body: { groupExternalId, csv, fileName: file?.name },
      });
      setJob(r.job);
      timer.current = setInterval(async () => {
        try {
          const { job: j } = await api(`/${partnerId}/imports/${r.job.id}`);
          setJob(j);
          if (j.status !== "running") {
            clearInterval(timer.current);
            onDone();
          }
        } catch {
          // keep polling — a blip shouldn't stop the progress view
        }
      }, 3000);
    } catch (e) {
      Swal.fire("Import failed to start", e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const group = groups.find((g) => g.externalId === groupExternalId);
  const pct = job
    ? Math.round((100 * job.processed) / Math.max(job.total, 1))
    : 0;

  return (
    <Modal
      isOpen
      size="lg"
      toggle={job?.status === "running" ? undefined : onClose}
    >
      <ModalHeader toggle={job?.status === "running" ? undefined : onClose}>
        Import members (CSV)
      </ModalHeader>
      <ModalBody>
        {!job ? (
          <>
            <FormGroup>
              <Label className="fw-semibold">Employer group</Label>
              <Input
                type="select"
                value={groupExternalId}
                onChange={(e) => setGroupExternalId(e.target.value)}
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.externalId}>
                    {g.name} ({g.externalId}) — {g.enrollmentMode} enrollment
                  </option>
                ))}
              </Input>
              {group && (
                <div className="small text-muted mt-1">
                  {group.enrollmentMode === "direct"
                    ? "Direct: BaxRx creates each member's pharmacy eligibility from the file. No emails are sent to members."
                    : "Portal: members are added and emailed an enrollment link; their eligibility is created when they sign up."}
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label className="fw-semibold">CSV file</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => pick(e.target.files?.[0] || null)}
              />
              <div className="small text-muted mt-1">
                One row per person; family rows name their primary in{" "}
                <code>primaryExternalId</code>.{" "}
                <a
                  href="#template"
                  onClick={async (ev) => {
                    ev.preventDefault();
                    const res = await fetch(
                      `${API_BASE}/dashboard/admin/partners/members-csv-template`,
                      {
                        headers: await authHeaders(),
                      },
                    );
                    download("members-template.csv", await res.text());
                  }}
                >
                  Download the template
                </a>
                . Re-importing the same file updates members instead of
                duplicating them.
              </div>
            </FormGroup>
            {busy && !preview && <Spinner size="sm" color="primary" />}
            {preview && (
              <div className="border rounded p-3 bg-light">
                <div>
                  <strong>{preview.primaries}</strong> members +{" "}
                  <strong>{preview.familyMembers}</strong> family members from{" "}
                  {preview.rows} rows.
                </div>
                {preview.errors.length > 0 ? (
                  <div className="mt-2">
                    <div className="text-warning fw-semibold">
                      {preview.errors.length} row(s) will be skipped:
                    </div>
                    <ul
                      className="small mb-0 ps-3"
                      style={{ maxHeight: 160, overflow: "auto" }}
                    >
                      {preview.errors.slice(0, 50).map((e, i) => (
                        <li key={i}>
                          Line {e.line}
                          {e.externalId ? ` (${e.externalId})` : ""}:{" "}
                          {e.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-success small mt-1">
                    Every row is valid.
                  </div>
                )}
                {preview.warnings?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-warning fw-semibold">
                      {preview.warnings.length} value(s) will be dropped — those
                      members load but stay "Missing data" until fixed:
                    </div>
                    <ul
                      className="small mb-0 ps-3"
                      style={{ maxHeight: 160, overflow: "auto" }}
                    >
                      {preview.warnings.slice(0, 50).map((w, i) => (
                        <li key={i}>
                          Line {w.line}
                          {w.externalId ? ` (${w.externalId})` : ""}:{" "}
                          {w.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="d-flex justify-content-between mb-1">
              <span>
                {job.status === "running"
                  ? "Importing…"
                  : job.status === "done"
                    ? "Import finished"
                    : "Import failed"}
              </span>
              <span>
                {job.processed} / {job.total}
              </span>
            </div>
            <Progress
              value={pct}
              color={
                job.status === "failed"
                  ? "danger"
                  : job.status === "done"
                    ? "success"
                    : "primary"
              }
              animated={job.status === "running"}
            />
            <div className="row text-center mt-3">
              <div className="col">
                <div className="fs-4 fw-semibold">{job.created}</div>
                <div className="small text-muted">created</div>
              </div>
              <div className="col">
                <div className="fs-4 fw-semibold">{job.updated}</div>
                <div className="small text-muted">updated</div>
              </div>
              <div className="col">
                <div className="fs-4 fw-semibold text-danger">{job.failed}</div>
                <div className="small text-muted">failed</div>
              </div>
              {Object.entries(job.eligibility || {}).map(([k, v]) => (
                <div className="col" key={k}>
                  <div className="fs-4 fw-semibold">{v}</div>
                  <div className="small text-muted">
                    {ELIGIBILITY[k]?.label || k}
                  </div>
                </div>
              ))}
            </div>
            {job.error && (
              <div className="text-danger small mt-2">{job.error}</div>
            )}
            {job.errors?.length > 0 && (
              <ul
                className="small mt-3 ps-3"
                style={{ maxHeight: 160, overflow: "auto" }}
              >
                {job.errors.slice(0, 100).map((e, i) => (
                  <li key={i}>
                    {e.line ? `Line ${e.line} ` : ""}
                    {e.externalId ? `(${e.externalId}) ` : ""}
                    {e.message}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {!job ? (
          <>
            <Button color="light" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={
                !preview || !preview.primaries || !groupExternalId || busy
              }
              onClick={start}
            >
              Import {preview?.primaries || ""} members
            </Button>
          </>
        ) : (
          <Button
            color="primary"
            disabled={job.status === "running"}
            onClick={onClose}
          >
            {job.status === "running" ? "Running in the background…" : "Close"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

// ── New employer group ─────────────────────────────────────────────────────

const GroupModal = ({ partnerId, onClose, onDone }) => {
  const [form, setForm] = useState({
    name: "",
    externalId: "",
    brokerLinkToken: "",
    enrollmentMode: "direct",
    coverageEffectiveDate: "",
    makeDefault: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api(`/${partnerId}/groups`, {
        method: "POST",
        body: {
          name: form.name.trim(),
          externalId: form.externalId.trim(),
          brokerLinkToken: form.brokerLinkToken.trim(),
          enrollmentMode: form.enrollmentMode,
          makeDefault: form.makeDefault,
          ...(form.coverageEffectiveDate
            ? {
                coverageEffectiveDate: Date.parse(
                  `${form.coverageEffectiveDate}T00:00:00Z`,
                ),
              }
            : {}),
        },
      });
      Swal.fire(
        "Group saved",
        `${r.group.name} (${r.group.enrollmentMode} enrollment)`,
        "success",
      );
      onDone(r.group);
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen toggle={onClose}>
      <ModalHeader toggle={onClose}>Employer group</ModalHeader>
      <ModalBody>
        <p className="small text-muted">
          Creates the group, or updates it if this external ID already exists
          for the partner.
        </p>
        <FormGroup>
          <Label className="fw-semibold">Name</Label>
          <Input
            value={form.name}
            onChange={set("name")}
            placeholder="e.g. Acme Corp"
          />
        </FormGroup>
        <FormGroup>
          <Label className="fw-semibold">External ID</Label>
          <Input
            value={form.externalId}
            onChange={set("externalId")}
            placeholder="The partner's id for this employer"
          />
        </FormGroup>
        <FormGroup>
          <Label className="fw-semibold">
            Connect ID of the broker / GA / National it belongs under
          </Label>
          <Input
            value={form.brokerLinkToken}
            onChange={set("brokerLinkToken")}
            placeholder="baxlink_…"
          />
        </FormGroup>
        <FormGroup>
          <Label className="fw-semibold">Enrollment</Label>
          <Input
            type="select"
            value={form.enrollmentMode}
            onChange={set("enrollmentMode")}
          >
            <option value="direct">
              Direct — BaxRx enrolls members from the data, no emails, no portal
            </option>
            <option value="portal">
              Portal — members sign up themselves via an emailed link
            </option>
          </Input>
        </FormGroup>
        <FormGroup>
          <Label className="fw-semibold">
            Coverage start{" "}
            <span className="text-muted">(optional, may be in the past)</span>
          </Label>
          <Input
            type="date"
            value={form.coverageEffectiveDate}
            onChange={set("coverageEffectiveDate")}
          />
        </FormGroup>
        <FormGroup check>
          <Input
            type="checkbox"
            id="group-make-default"
            checked={form.makeDefault}
            onChange={(e) =>
              setForm((f) => ({ ...f, makeDefault: e.target.checked }))
            }
          />
          <Label check for="group-make-default">
            Use this group for the partner's <strong>Members API</strong> (
            <code>/members</code>) — for a partner that is itself the employer
          </Label>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={
            busy || !form.name || !form.externalId || !form.brokerLinkToken
          }
          onClick={save}
        >
          {busy ? "Saving…" : "Save group"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ── The panel ──────────────────────────────────────────────────────────────

/**
 * Every member a partner integration brought in, manageable from here: filter
 * by group, coverage status and eligibility; open one to see their card data
 * and family, edit them, terminate or reactivate, retry a failed eligibility;
 * bulk-load a CSV; export the list (with member ids) as a CSV.
 */
const PartnerMembers = ({ partners, fixedPartnerId }) => {
  const [partnerId, setPartnerId] = useState(fixedPartnerId || "");
  const [groups, setGroups] = useState([]);
  const [savingDefault, setSavingDefault] = useState(false);
  const [filters, setFilters] = useState({
    groupExternalId: "",
    status: "",
    eligibility: "",
    q: "",
  });
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openMember, setOpenMember] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showGroup, setShowGroup] = useState(false);

  // Default to the partner holding the most members.
  useEffect(() => {
    if (!partnerId && partners.length) {
      const top = [...partners].sort(
        (a, b) => (b.employeesCount || 0) - (a.employeesCount || 0),
      )[0];
      setPartnerId(top.id);
    }
  }, [partners, partnerId]);

  const loadGroups = useCallback(async () => {
    if (!partnerId) return;
    try {
      setGroups((await api(`/${partnerId}/groups`)).groups || []);
    } catch (e) {
      Swal.fire("Could not load groups", e.message, "error");
    }
  }, [partnerId]);

  const params = useMemo(() => {
    const p = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    return p.toString();
  }, [filters, page]);

  const loadMembers = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      setData(await api(`/${partnerId}/members?${params}`));
    } catch (e) {
      Swal.fire("Could not load members", e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [partnerId, params]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);
  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      setFilters((f) => ({ ...f, q: query }));
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const setFilter = (k, v) => {
    setPage(0);
    setFilters((f) => ({ ...f, [k]: v }));
  };

  const exportCsv = async () => {
    try {
      const p = new URLSearchParams({ page: "0", pageSize: "5000" });
      Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
      const all = await api(`/${partnerId}/members?${p.toString()}`);
      const rows = all.members.map((m) => ({
        externalId: m.externalId,
        firstName: m.firstName,
        lastName: m.lastName,
        email: m.email,
        group: m.groupName,
        status: m.status,
        enrollmentMode: m.enrollmentMode,
        eligibility: eligibilityKey(m),
        memberId: m.eligibility?.memberId || "",
        coverageStart:
          m.eligibility?.startDate || fmtDay(m.coverageEffectiveDate),
        coverageEnd: m.eligibility?.endDate || fmtDay(m.terminationDate),
        missingFields: (m.eligibility?.missingFields || []).join(" "),
        error: m.eligibility?.error || "",
        familyMembers: m.dependentsCount,
      }));
      const partner = partners.find((x) => x.id === partnerId);
      download(
        `${(partner?.slug || partner?.name || "partner").replace(/\W+/g, "-")}-members.csv`,
        toCsv(rows, Object.keys(rows[0] || { externalId: "" })),
      );
    } catch (e) {
      Swal.fire("Export failed", e.message, "error");
    }
  };

  const summary = data?.summary;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Card className="mt-3">
      <CardHeader className="d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <h4 className="mb-0">Members</h4>
          <p className="text-muted mb-0 mt-1">
            Every member a partner brought in. Open one to see their card data,
            edit, terminate or reactivate. Changes made here behave exactly like
            the partner making them.
          </p>
        </div>
        <div className="d-flex gap-2">
          {!fixedPartnerId && (
            <Input
              type="select"
              bsSize="sm"
              style={{ minWidth: 220 }}
              value={partnerId}
              onChange={(e) => {
                setPartnerId(e.target.value);
                setPage(0);
                setFilters({
                  groupExternalId: "",
                  status: "",
                  eligibility: "",
                  q: "",
                });
                setQuery("");
              }}
            >
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.employeesCount || 0})
                </option>
              ))}
            </Input>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {groups.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 border rounded bg-light small">
            <span className="fw-semibold">Members API account:</span>
            <Input
              type="select"
              bsSize="sm"
              style={{ width: "auto", minWidth: 220 }}
              value={groups.find((g) => g.isDefault)?.externalId || ""}
              disabled={savingDefault}
              onChange={async (e) => {
                setSavingDefault(true);
                try {
                  await api(`/${partnerId}/default-group`, {
                    method: "PUT",
                    body: { groupExternalId: e.target.value || null },
                  });
                  await loadGroups();
                } catch (err) {
                  Swal.fire("Error", err.message, "error");
                } finally {
                  setSavingDefault(false);
                }
              }}
            >
              <option value="">— None —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.externalId}>
                  {g.name} ({g.externalId})
                </option>
              ))}
            </Input>
            <span className="text-muted">
              The group behind <code>/members</code> — for a partner that is
              itself the employer and works with members instead of groups and
              employees.
            </span>
          </div>
        )}
        <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
          <div style={{ minWidth: 220 }}>
            <Label className="small mb-1">Group</Label>
            <Input
              type="select"
              bsSize="sm"
              value={filters.groupExternalId}
              onChange={(e) => setFilter("groupExternalId", e.target.value)}
            >
              <option value="">All groups ({groups.length})</option>
              {groups.map((g) => (
                <option key={g.id} value={g.externalId}>
                  {g.isDefault ? "★ " : ""}
                  {g.name} · {g.enrollmentMode} · {g.employeesCount}
                </option>
              ))}
            </Input>
          </div>
          <div>
            <Label className="small mb-1">Status</Label>
            <Input
              type="select"
              bsSize="sm"
              value={filters.status}
              onChange={(e) => setFilter("status", e.target.value)}
            >
              <option value="">Any</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v} {summary?.byStatus?.[k] ? `(${summary.byStatus[k]})` : ""}
                </option>
              ))}
            </Input>
          </div>
          <div style={{ minWidth: 240, flex: 1 }}>
            <Label className="small mb-1">Search</Label>
            <Input
              bsSize="sm"
              placeholder="Name, email, external ID or member ID"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="ms-auto d-flex gap-2">
            <Button
              size="sm"
              color="light"
              onClick={() => setShowGroup(true)}
              disabled={!partnerId}
            >
              New group
            </Button>
            <Button
              size="sm"
              color="primary"
              onClick={() => setShowImport(true)}
              disabled={!groups.length}
            >
              Import CSV
            </Button>
            <Button
              size="sm"
              color="light"
              onClick={exportCsv}
              disabled={!data?.total}
            >
              Export CSV
            </Button>
          </div>
        </div>

        {summary && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button
              size="sm"
              color={!filters.eligibility ? "dark" : "light"}
              onClick={() => setFilter("eligibility", "")}
            >
              All{" "}
              <Badge color="secondary" className="ms-1">
                {summary.total}
              </Badge>
            </Button>
            {Object.entries(summary.byEligibility || {}).map(([k, v]) => (
              <Button
                key={k}
                size="sm"
                color={filters.eligibility === k ? "dark" : "light"}
                onClick={() =>
                  setFilter("eligibility", filters.eligibility === k ? "" : k)
                }
              >
                {ELIGIBILITY[k]?.label || k}{" "}
                <Badge
                  color={ELIGIBILITY[k]?.color || "secondary"}
                  className="ms-1"
                >
                  {v}
                </Badge>
              </Button>
            ))}
          </div>
        )}

        {loading && !data ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : !data?.members?.length ? (
          <p className="text-muted mb-0">No members match.</p>
        ) : (
          <>
            <div
              className="table-responsive"
              style={{ opacity: loading ? 0.6 : 1 }}
            >
              <Table hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Member</th>
                    <th>Group</th>
                    <th>Status</th>
                    <th>Coverage</th>
                    <th>Eligibility</th>
                    <th>Member ID</th>
                    <th className="text-center">Family</th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.map((m) => (
                    <tr
                      key={m.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setOpenMember(m.externalId)}
                    >
                      <td>
                        <div className="fw-semibold">
                          {m.firstName} {m.lastName}
                        </div>
                        <div className="text-muted small">
                          {m.externalId} · {m.email}
                        </div>
                      </td>
                      <td className="small">
                        {m.groupName}
                        <div className="text-muted">{m.enrollmentMode}</div>
                      </td>
                      <td>
                        <Badge
                          color={STATUS_COLORS[m.status] || "secondary"}
                          className="fw-normal"
                        >
                          {STATUS_LABELS[m.status] || m.status}
                        </Badge>
                      </td>
                      <td className="small text-nowrap">
                        {m.eligibility?.startDate ||
                          fmtDay(m.coverageEffectiveDate)}
                        {" → "}
                        {m.eligibility?.endDate &&
                        m.eligibility.endDate !== "9999-12-31"
                          ? m.eligibility.endDate
                          : m.terminationDate
                            ? fmtDay(m.terminationDate)
                            : "open"}
                      </td>
                      <td>
                        <EligibilityBadge member={m} />
                        {m.eligibility?.missingFields?.length > 0 && (
                          <div
                            className="small text-warning"
                            title={m.eligibility.missingFields.join(", ")}
                          >
                            {m.eligibility.missingFields.length} field(s)
                            missing
                          </div>
                        )}
                      </td>
                      <td className="fw-semibold small">
                        {m.eligibility?.memberId || "—"}
                      </td>
                      <td className="text-center">{m.dependentsCount || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3 small">
              <span className="text-muted">
                {data.total} member{data.total === 1 ? "" : "s"}
              </span>
              <div className="d-flex gap-2 align-items-center">
                <Button
                  size="sm"
                  color="light"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <span>
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  size="sm"
                  color="light"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </CardBody>

      {openMember && (
        <MemberDetailModal
          partnerId={partnerId}
          externalId={openMember}
          onClose={() => setOpenMember(null)}
          onChanged={loadMembers}
        />
      )}
      {showImport && (
        <ImportModal
          partnerId={partnerId}
          groups={groups}
          onClose={() => setShowImport(false)}
          onDone={() => {
            loadMembers();
            loadGroups();
          }}
        />
      )}
      {showGroup && (
        <GroupModal
          partnerId={partnerId}
          onClose={() => setShowGroup(false)}
          onDone={() => {
            setShowGroup(false);
            loadGroups();
          }}
        />
      )}
    </Card>
  );
};

export default PartnerMembers;
